import React from "react";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core";
import { fade } from "@material-ui/core/styles";
import checkLevel from "../assets/check_level.svg";

const valueColors = ["#F19B36", "#E6D011", "#72BB7D", "#72BB7D", "#909293"];

const useStyles = makeStyles((theme) => ({
  percentText: {
    fontSize: 20,
    margin: "0 0 0 5px",
    fontWeight: 500,
    color: ({ progressColor, colorText }) => (colorText ? progressColor : "inherit"),
  },
  backCircle: {
    color: theme.palette.grey[200],
    position: "absolute",
  },
  frontCircle: {
    color: ({ progressColor }) => progressColor,
  },
  pathQuadrat: {
    margin: -3,
  },
  checkIconBox: {
    width: 28,
    height: 28,
    border: `2px solid ${fade("#226C36", 0.2)}`,
    backgroundColor: "#B0F2B6",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    top: 31,
    left: 31,
  },
}));

/**
 * Показывает фактический прогресс, позволяя отключить центральную отметку завершения для карточек уроков.
 *
 *  @param {String} className
 * @param {String} [textValue]
 * @param {Boolean} isLock
 * @param {Boolean} colorText
 * @param {Object} rest
 * @returns {*}
 * @constructor
 */
function QuadraticProgress({ className, textValue, isLock, colorText, showCompletionBadge = true, ...rest }) {
  const { value } = rest;
  let progressColor;
  if (value === 0) {
    progressColor = valueColors[4];
  } else if (value < 30) {
    progressColor = valueColors[0];
  } else if (value < 70) {
    progressColor = valueColors[1];
  } else if (value < 100) {
    progressColor = valueColors[2];
  } else {
    progressColor = valueColors[3];
  }

  const classes = useStyles({ progressColor, colorText });

  return (
    <Box className={className} position="relative" display="inline-flex" {...rest}>
      {(value !== 100 || !colorText || !showCompletionBadge) && (
        <svg className={classes.pathQuadrat} viewBox="0 0 100 100">
          <path
            d="
              M50,5
              H73
              Q95,5 95,27
              V73
              Q95,95 73,95
              H27
              Q5,95 5,73
              V27
              Q5,5 27,5
              H50 Z"
            fill="none"
            stroke={`${progressColor}`}
            strokeWidth="4"
            strokeLinejoin="round"
            strokeLinecap="round"
            strokeDasharray="340"
            strokeDashoffset={`calc(340 - 340 * ${value} / 100)`}
          />
        </svg>
      )}
      <Box
        top={0}
        left={0}
        bottom={0}
        right={0}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {isLock ? (
          <svg width="20" height="20" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M6 0.47462C6.35232 0.47462 6.58676 0.473165 6.79102 0.503917C7.92983 0.675566 8.8244 1.5702 8.99609 2.709C9.02684 2.91324 9.02539 3.14774 9.02539 3.50001V3.792C9.84803 4.08878 10.4879 4.7541 10.749 5.59376C10.8753 5.99989 10.875 6.50002 10.875 7.50001C10.875 8.49992 10.8752 9.00015 10.749 9.40626C10.4755 10.2858 9.78675 10.9754 8.90723 11.249C8.50104 11.3754 8.00025 11.375 7 11.375H5C4.00003 11.375 3.49988 11.3753 3.09375 11.249C2.21406 10.9754 1.52555 10.2859 1.25195 9.40626C1.1257 9.00012 1.125 8.50005 1.125 7.50001C1.125 6.49989 1.12565 5.99992 1.25195 5.59376C1.51307 4.75422 2.15214 4.08884 2.97461 3.792V3.50001C2.97461 3.14774 2.97315 2.91324 3.00391 2.709C3.1756 1.5702 4.07017 0.675566 5.20898 0.503917C5.41324 0.473165 5.64768 0.47462 6 0.47462ZM6 6.62501C5.79302 6.62512 5.62505 6.79301 5.625 7.00001V8.00001C5.625 8.20705 5.79298 8.37491 6 8.37501C6.20711 8.37501 6.375 8.20712 6.375 8.00001V7.00001C6.37495 6.79295 6.20707 6.62501 6 6.62501ZM6 1.5254C5.60934 1.5254 5.47214 1.52687 5.36523 1.54298C4.68202 1.64599 4.14602 2.18205 4.04297 2.86525C4.02686 2.97214 4.02539 3.10941 4.02539 3.50001V3.63478C4.28914 3.62703 4.60568 3.62501 5 3.62501H7C7.39422 3.62501 7.71089 3.62704 7.97461 3.63478V3.50001C7.97461 3.10941 7.97314 2.97214 7.95703 2.86525C7.85398 2.18205 7.31798 1.64599 6.63477 1.54298C6.52786 1.52687 6.39066 1.5254 6 1.5254Z"
              fill="#464748"
            />
          </svg>
        ) : value === 100 && colorText && showCompletionBadge ? (
          <Box className={classes.checkIconBox}>
            <img src={checkLevel} alt="checkLevel" width={18} height={18} />
          </Box>
        ) : (
          <Typography className={classes.percentText} variant="subtitle2" component="div">
            {textValue || `${Math.round(value)}%`}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

QuadraticProgress.propTypes = {
  /**
   * @type Validator<Number>
   * The value of the progress indicator for the determinate variant.
   * Value between 0 and 100.
   */
  value: PropTypes.number.isRequired,
  /** @type Validator<String> */
  className: PropTypes.string,
  textValue: PropTypes.string,
  isLock: PropTypes.bool,
  colorText: PropTypes.bool,
  /** Показывает центральную отметку при 100%, если карточка не выводит отдельную галочку зачёта. */
  showCompletionBadge: PropTypes.bool,
};

export default QuadraticProgress;
